import { Link } from 'react-router-dom';
import { ChevronDown, Lock, RotateCcw } from 'lucide-react';
import { ROLE_COLORS, ROLE_LABELS } from './navConstants';

/**
 * Mobile slide-down menu — opens when the hamburger is tapped.
 * Mirrors the desktop links + user menu in a single vertical list.
 */
export default function MobileMenu({
  user, hasRole, isActive, navigate, handleLogout,
  setMobileMenuOpen, setShowPasswordDialog,
  inContext, isInOrgView, operatorContext, handleResetContext,
  visibleOrgs, activeOrgId, setActiveOrgId,
  companies, handleCompanySelect,
  mobileCompaniesOpen, setMobileCompaniesOpen,
  trialDays,
}) {
  const mobileNavLink = (path, label) => (
    <Link
      to={path}
      onClick={() => setMobileMenuOpen(false)}
      className={`block px-4 py-3 text-sm transition-colors border-b border-gray-100 ${
        isActive(path) ? 'bg-gray-200 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-100'
      }`}
      data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {label}
    </Link>
  );

  const mobileCompaniesSection = () => (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setMobileCompaniesOpen(!mobileCompaniesOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors ${
          isActive('/companies') ? 'bg-gray-200 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-100'
        }`}
        data-testid="mobile-nav-companies"
      >
        Company Entries
        <ChevronDown className={`w-4 h-4 transition-transform ${mobileCompaniesOpen ? 'rotate-180' : ''}`} />
      </button>
      {mobileCompaniesOpen && (
        <div className="bg-gray-50">
          {companies.length > 0 ? companies.map(c => (
            <button
              key={c.name}
              onClick={() => { handleCompanySelect(c.name); setMobileMenuOpen(false); setMobileCompaniesOpen(false); }}
              className="w-full text-left px-6 py-2.5 text-sm text-gray-600 hover:bg-gray-100 border-b border-gray-100 last:border-0"
              data-testid={`mobile-company-${c.name}`}
            >
              {c.name}
            </button>
          )) : (
            <p className="px-6 py-3 text-xs text-gray-400">No companies available</p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="md:hidden mt-3 -mx-4 bg-white border-t border-gray-200 shadow-lg" data-testid="mobile-menu">
      {/* User info */}
      {user && (
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700">
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
            {inContext && (
              <p className="text-[10px] text-teal-600 font-medium truncate">Viewing: {operatorContext.tenantName}{operatorContext.orgName ? ` / ${operatorContext.orgName}` : ''}</p>
            )}
            {isInOrgView && (
              <p className="text-[10px] text-indigo-600 font-medium truncate">{visibleOrgs.find(o => o.org_id === activeOrgId)?.name || 'Organization'}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {inContext && (
              <button onClick={handleResetContext} className="p-1 rounded hover:bg-teal-100 text-teal-500" title="Reset to AAC" data-testid="mobile-reset-context">
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            {isInOrgView && (
              <button onClick={() => { setActiveOrgId(null); setMobileMenuOpen(false); }} className="p-1 rounded hover:bg-indigo-100 text-indigo-500" title="Reset to All Orgs" data-testid="mobile-reset-org">
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[user.role]}`}>
              {ROLE_LABELS[user.role]}
            </span>
          </div>
        </div>
      )}

      {/* Nav links */}
      {user?.is_platform_operator ? (
        <>
          {mobileNavLink('/', 'Home')}
          {inContext && hasRole('super_admin', 'admin', 'manager', 'member') && mobileNavLink('/intake', 'Intake')}
          {inContext && mobileNavLink('/all-entries', 'All Entries')}
          {inContext && mobileCompaniesSection()}
          {mobileNavLink('/support', 'Support Queue')}
          {inContext && mobileNavLink('/admin', 'Company Management')}
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager') && mobileNavLink('/user-management', 'User Management')}
          {(user?.role === 'super_admin' || user?.role === 'admin') && mobileNavLink('/audit-log', 'Audit Log')}
          {inContext && (user?.role === 'super_admin' || user?.role === 'admin') && mobileNavLink('/recycle-bin', 'Recycle Bin')}
          {inContext && hasRole('super_admin', 'admin') && (
            <Link
              to="/settings?tab=billing"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm transition-colors border-b border-gray-100 text-gray-600 hover:bg-gray-100"
              data-testid="mobile-nav-billing"
            >
              Billing
            </Link>
          )}
          {user?.is_developer && mobileNavLink('/dev-hub', 'Dev Hub')}
          {hasRole('super_admin', 'admin') && mobileNavLink('/settings', 'Settings')}
        </>
      ) : (
        <>
          {mobileNavLink('/', 'Home')}
          {hasRole('super_admin', 'admin', 'manager', 'member') && mobileNavLink('/intake', 'Intake')}
          {mobileNavLink('/all-entries', 'All Entries')}
          {mobileCompaniesSection()}

          {/* V2: Mobile Org Switcher */}
          {user?.account_type === 'provider' && visibleOrgs.length > 0 && (
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Organization</p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setActiveOrgId(null)}
                  className={`px-2.5 py-1 rounded text-xs ${!activeOrgId ? 'bg-indigo-100 text-indigo-700 font-medium' : 'bg-gray-100 text-gray-600'}`}
                  data-testid="mobile-org-switch-all"
                >
                  All Orgs
                </button>
                {visibleOrgs.map(org => (
                  <button
                    key={org.org_id}
                    onClick={() => setActiveOrgId(org.org_id)}
                    className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 ${activeOrgId === org.org_id ? 'bg-indigo-100 text-indigo-700 font-medium' : 'bg-gray-100 text-gray-600'}`}
                    data-testid={`mobile-org-switch-${org.org_id}`}
                  >
                    <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: org.branding_color || '#6366f1' }} />
                    {org.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasRole('super_admin', 'admin', 'manager') && mobileNavLink('/admin', 'Company Management')}
          {((user?.account_type === 'provider') || (inContext && operatorContext?.accountType === 'provider')) && hasRole('super_admin', 'admin') && !isInOrgView && mobileNavLink('/organizations', 'Organizations')}
          {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager') && mobileNavLink('/user-management', 'User Management')}
          {(user?.role === 'super_admin' || user?.role === 'admin') && mobileNavLink('/audit-log', 'Audit Log')}
          {(user?.role === 'super_admin' || user?.role === 'admin') && mobileNavLink('/recycle-bin', 'Recycle Bin')}
          {hasRole('super_admin', 'admin') && mobileNavLink('/settings', 'Settings')}
          {hasRole('super_admin', 'admin') && (
            <Link
              to="/settings?tab=billing"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm transition-colors border-b border-gray-100 text-gray-600 hover:bg-gray-100 flex items-center justify-between"
              data-testid="mobile-nav-billing"
            >
              <span>Billing</span>
              {trialDays !== null && (
                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{trialDays}d trial left</span>
              )}
            </Link>
          )}
          {user?.is_developer && mobileNavLink('/dev-hub', 'Dev Hub')}
        </>
      )}

      {/* Change/Set Password */}
      <button
        onClick={() => { setMobileMenuOpen(false); setShowPasswordDialog(true); }}
        className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 transition-colors border-t border-gray-100 flex items-center gap-2"
        data-testid="mobile-change-password"
      >
        <Lock className="w-4 h-4" />
        {user?.has_local_password === false ? 'Set Password' : 'Change Password'}
      </button>

      {/* Logout */}
      <button
        onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
        className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
        data-testid="mobile-logout-btn"
      >
        Sign Out
      </button>
    </div>
  );
}
